import * as React from 'react'
import { styled } from 'next-yak'

import * as model from '@owl-nest/models'
import { MoneyDisplayer } from '@owl-nest/money'
import * as plume from '@ulule/owl-kit-components/next'

import * as projectModel from '../../../../models/project'

type RewardListPropTypes = {
  displayDetail: boolean
  skuList?: model.sku.Sku<projectModel.ExtraFields>[]
  subtitle?: string
  title: string
}

export function RewardList({
  displayDetail,
  skuList = [],
  subtitle,
  title,
}: RewardListPropTypes): React.ReactElement<RewardListPropTypes> {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const additionalCost =
    skuList?.length === 1 ? skuList[0].type === 'variant' && skuList[0].item.additional_cost : undefined

  return (
    <RewardListContainer>
      {displayDetail ? (
        <Rewards>
          <OpenDetails $isExpanded={isExpanded} onClick={toggleIsExpanded}>
            <plume.styles.heading.XXXS>{title}</plume.styles.heading.XXXS>
            <plume.glyphs.stroke.CaretDown size={12} />
          </OpenDetails>
          <RewardsListDetail $isExpanded={isExpanded}>
            {skuList.map((sku, i) => {
              const additionalCost = sku.type === 'variant' && sku.item.additional_cost
              return (
                <RewardsListItem key={i}>
                  {sku.quantity > 1 && <RewardQuantity>{sku.quantity}x</RewardQuantity>}
                  <RewardTitle>
                    {model.sku.title(sku)}
                    {additionalCost && (
                      <>
                        {'\u00A0'}(+{'\u00A0'}
                        <MoneyDisplayer amount={additionalCost} currency={sku.item.parent.project.currency} />)
                      </>
                    )}
                  </RewardTitle>
                </RewardsListItem>
              )
            })}
          </RewardsListDetail>
        </Rewards>
      ) : (
        <>
          <plume.styles.heading.XXXS>{title}</plume.styles.heading.XXXS>
          {subtitle && (
            <plume.styles.copy.XS.regular>
              {subtitle}
              {skuList[0].type === 'variant' && additionalCost && (
                <>
                  {'\u00A0'}(+{'\u00A0'}
                  <MoneyDisplayer amount={additionalCost} currency={skuList[0].item.parent.project.currency} />)
                </>
              )}
            </plume.styles.copy.XS.regular>
          )}
        </>
      )}
    </RewardListContainer>
  )

  function toggleIsExpanded(): void {
    setIsExpanded(!isExpanded)
  }
}

const RewardListContainer = styled.div`
  flex-grow: 1;
  padding: 0 8px;
  max-width: calc(100% - 57px);
  box-sizing: border-box;
  text-align: left;

  ${plume.styles.heading.XXXS} {
    max-width: 298px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const OpenDetails = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: baseline;

  ${plume.glyphs.stroke.CaretDown} {
    display: inline-block;
    position: relative;
    margin-left: 5px;
    transform: ${(props) => (props.$isExpanded === true ? 'rotateX(180deg)' : 'none')};
    height: 7px;
  }
`

const Rewards = styled.div`
  ${plume.styles.heading.XXXS} {
    cursor: pointer;
    display: inline-block;
    user-select: none;
  }
`

const RewardTitle = styled(plume.styles.copy.XS.regular)`
  // yak does not give a class to non exported styled-component with no css.
  // this is an issue because this component is used as a selector later in the file
  // to fix this we need to add a bogus css rule to force yak to generate a class
  // see : https://github.com/DigitecGalaxus/next-yak/issues/373
  --bogus-style: 0;
`

const RewardQuantity = styled(plume.styles.copy.XS.regular)`
  // yak does not give a class to non exported styled-component with no css.
  // this is an issue because this component is used as a selector later in the file
  // to fix this we need to add a bogus css rule to force yak to generate a class
  // see : https://github.com/DigitecGalaxus/next-yak/issues/373
  --bogus-style: 0;
`

const RewardsListItem = styled.li`
  position: relative;

  ${RewardQuantity} {
    position: absolute;
    left: -10px;
    transform: translateX(-100%);
    line-height: 18px;
  }

  ${RewardTitle} {
    line-height: 18px;
    white-space: nowrap;
    max-width: calc(100% - 20px);
    text-overflow: ellipsis;
    overflow: hidden;

    & + ${RewardTitle} {
      margin-top: 3px;
    }
  }
`

const RewardsListDetail = styled.ul<{ $isExpanded: boolean }>`
  list-style: none;
  margin: 3px 0 10px;
  padding: 0;
  display: ${(props) => (props.$isExpanded ? 'block' : 'none')};
`
